import { ReactElement, useEffect, ComponentType } from 'react'
import { Route, RouteProps } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import ContentLoading from './ContentLoading'

interface PrivateComponentProps {
  component: ComponentType
}

// Inspired by what this does https://auth0.github.io/auth0-react/modules/with_authentication_required.html
// Adds a hasToken check in addition to auth check.
// Avoids creating a completely new component on each render call
// (which seriously messes up children and causes expensive re-rendering)
function PrivateRouteComponent(props: PrivateComponentProps): JSX.Element {
  const { component: Component } = props
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth()
  const returnTo = `${window.location.pathname}${window.location.search}`

  useEffect(() => {
    if (isLoading || isAuthenticated) return

    loginWithRedirect({
      returnTo,
    })
  }, [isLoading, isAuthenticated, loginWithRedirect, returnTo])

  return isAuthenticated && !isLoading ? <Component /> : <ContentLoading />
}

interface Props {
  exact?: RouteProps['exact']
  path: RouteProps['path']
  component: ComponentType
}

// PrivateRoute from https://auth0.com/blog/complete-guide-to-react-user-authentication/#Protecting-Routes
export const PrivateRoute = (props: Props): ReactElement => {
  const { exact, path, component } = props

  return (
    <Route
      exact={exact}
      path={path}
      render={() => <PrivateRouteComponent component={component} />}
    />
  )
}

export default PrivateRoute
